/*
 * Copyright (c) 2025.  Chad and contributors.
 * All rights reserved.
 */

import {computed} from 'vue';
import {addGtag, configure} from 'vue-gtag';
import {useCompany} from '@/composables/useCompany';
import type {BookingInterface} from '@/types/booking';
import {d} from '@/utils/consoleHelper.ts';
import {CENTS_TO_DOLLARS} from '@/constants/money';

export interface TrackingApp {
    app: string;
    enabled: boolean;
    identifier: 'google_analytics' | 'meta_pixel' | string;
    measurement_id?: string; // for GA
    pixel_id?: string; // for Meta, etc.
}

export function useAppTracking() {
    const {fleetAppSubscriptions} = useCompany();

    const googleAnalyticsApp = computed<TrackingApp | undefined>(() =>
        fleetAppSubscriptions.value.find((sub: TrackingApp) => sub.identifier === 'google_analytics')
    );

    const initGoogleAnalytics = async (): Promise<void> => {
        d.warn('🌐 Initializing Google Analytics...');
        const {isOnline} = await import('@/utils/network');
        if (!isOnline()) return;

        const ga = googleAnalyticsApp.value;
        if (ga?.enabled && ga.measurement_id) {
            try {
                configure({
                    tagId: ga.measurement_id,
                    config: {send_page_view: true},
                    initMode: 'manual',
                });

                await addGtag();
                d.log('✅ Google Analytics (gtag) Bootstrapped');
            } catch (err) {
                d.warn('⚠️ Google Analytics failed to load', err);
            }
        } else {
            d.warn('⚠️ Google Analytics not configured');
        }
    };

    const trackEvent = (event: string, data: Record<string, any> = {}) => {
        if (window.gtag && googleAnalyticsApp.value?.enabled) {
            window.gtag('event', event, data);
            d.log(`📊 Tracked event: ${event}`, data);
        }
    };

    const trackPurchase = (booking: BookingInterface) => {
        const trackedKey = `purchase_tracked_${booking.b_id}`;

        if (!sessionStorage.getItem(trackedKey)) {
            const orderLines = booking?.order?.lines || [];
            trackEvent('purchase', {
                transaction_id: booking.b_id,
                currency: 'USD',
                value: booking.total,
                items: orderLines.map((line) => ({
                    item_name: line.title,
                    item_id: line.item_id,
                    price: line.price_in_cents * CENTS_TO_DOLLARS,
                    quantity: line.quantity,
                })),
            });

            sessionStorage.setItem(trackedKey, 'true');
            d.log(`📦 Purchase tracked for booking ${booking.b_id}`);
        } else {
            d.log(`⚠️ Purchase already tracked for booking ${booking.b_id}`);
        }
    };


    return {
        initGoogleAnalytics,
        trackEvent,
        trackPurchase,
        googleAnalyticsApp,
    };
}
